import { browser } from './browser.js';
import { UIModal } from '../components/modal/ui-modal.js';
import { waitForAnimations } from './helpers.js';
import {
    type UIIFrameController,
    type UIModalController,
    type UIOverlayController,
} from '../components/index.js';

type OverlayHandler = (
    controller: UIIFrameController | UIModalController | UIOverlayController,
    event: CustomEvent | MediaQueryListEvent | Event,
    ...callbacks: Array<(...args: Array<unknown>) => void>
) => void;

/**
 * @type {string[]}
 */
export const OverlayWatchEvents: Array<string> = [
    'modal-updatelist',
    'modal-open-enter',
    'modal-close-enter',
    'menu-open',
    'menu-close',
    'menu-toggle',
    'popover-updated',
];

/**
 * Edge case when ui-nav inside ui-navtoggle.
 * @param {EventTarget | HTMLElement} target
 * @returns {boolean}
 */
export const shouldMenuToggle = (
    target: EventTarget | HTMLElement | null
): boolean => {
    if (target instanceof HTMLElement) {
        return !target.closest('ui-modal,ui-navtoggle');
    }
    return false;
};

/**
 * @param {UIIFrameController | UIOverlayController} controller
 * @returns {boolean}
 */
export const isDesktopMenu = (
    controller: UIIFrameController | UIModalController | UIOverlayController
): boolean => {
    return !!(controller.mainMenu?.dropdown ?? browser.desktopMediaMatches());
};

/**
 * Edge case when tooltip is the last popover.
 * @param {UIIFrameController | UIOverlayController} controller
 * @returns {boolean}
 */
export const isTooltipLast = (
    controller: UIIFrameController | UIModalController | UIOverlayController
): boolean => {
    const top = controller?.modalController.getActiveModals().pop();
    return !!top?.classList.contains('-tooltip');
};

/**
 * @param {UIModalController} modalController
 * @returns {boolean}
 */
export const hasLastOverlay = (modalController: UIModalController): boolean => {
    const predicate = (modal: UIModal) =>
        modal.classList.contains('-presented');
    return modalController.getActiveModals().filter(predicate).length > 0;
};

/**
 * @type {OverlayHandler}
 */
export const mainMenuOverlayHandler: OverlayHandler = (
    controller,
    event,
    openCallback,
    closeCallback
) => {
    const isMenuOnDesktop = isDesktopMenu(controller);
    switch (event.type) {
        case 'menu-open':
            if (
                !shouldMenuToggle(event.target) ||
                (controller.menuOpened && isMenuOnDesktop)
            ) {
                break;
            }
            openCallback({
                controller,
                isDesktopMenu: isMenuOnDesktop,
            });
            break;
        case 'menu-close':
            if (!shouldMenuToggle(event.target)) {
                break;
            }
            closeCallback({
                controller,
                isDesktopMenu: isMenuOnDesktop,
            });
            break;
    }
};

/**
 * @type {OverlayHandler}
 */
export const modalOverlayHandler: OverlayHandler = async (
    controller,
    event,
    enterCallback,
    exitCallback,
    updateCallback
) => {
    // Normal modals use event 'modal-open-enter' and 'modal-close-enter'.
    // All popover like uses mostly 'modal-updatelist'.
    const modal = event.target as UIModal;
    const isPopover = modal.type === UIModal.Type.Popover;
    const isFixedAlignment = modal.align === 'fixed';
    let modalAction = '';
    if (event instanceof CustomEvent && event.detail) {
        modalAction = String(event.detail?.action);
    }
    const isRemovedAction = modalAction === 'remove';
    const isAddedAction = modalAction === 'add';
    const isNavToggle = modal.classList.contains('-navtoggle');
    const isNotBubble = modal.type !== UIModal.Type.Bubble;

    await waitForAnimations(modal);
    const hasLastOverlayValue = hasLastOverlay(controller.modalController);

    const cbPayload = {
        controller,
        isNotBubble,
        isNavToggle: false,
        hasOverlay: hasLastOverlayValue,
        blockScroll: hasLastOverlayValue,
        partial: false,
    };

    switch (event.type) {
        case 'modal-open-enter':
            if (!isPopover && !isFixedAlignment) {
                enterCallback(cbPayload);
            }
            break;
        case 'modal-close-enter': {
            if (!isPopover && !isFixedAlignment) {
                exitCallback(cbPayload);
            }
            break;
        }
        case 'modal-updatelist':
            if (isPopover && isFixedAlignment) {
                const hasOverlay =
                    isAddedAction || !(isRemovedAction || isNavToggle);
                const blockScroll = isAddedAction || hasLastOverlayValue;
                updateCallback({ ...cbPayload, hasOverlay, blockScroll });
            }
            break;
    }
};

/**
 * @type {OverlayHandler}
 */
export const handleMenuOnResize: OverlayHandler = (
    controller,
    event,
    callback
) => {
    if (isTooltipLast(controller)) {
        return;
    }
    if (!('matches' in event)) {
        return;
    }
    // Really weird and complex menu/navtoggle logic.
    const isMobileMediaMatches = event.matches;
    const cbPayload = {
        controller,
        isMobileMediaMatches,
    };
    callback(cbPayload);
};

/**
 * @type {OverlayHandler}
 */
export const handleTooltipsOnResize: OverlayHandler = (
    controller,
    event,
    callback
) => {
    if (!isTooltipLast(controller)) {
        return;
    }
    if (!('matches' in event)) {
        return;
    }
    const isMobileMediaMatches = event.matches;
    const cbPayload = {
        controller,
        isMobileMediaMatches,
    };
    callback(cbPayload);
};

/**
 * @function OverlayHandler
 * @param {UIIFrameController | UIModalController | UIOverlayController} controller
 * @param {CustomEvent | MediaQueryListEvent | Event} event
 * @param {...Function} callbacks
 */
